import { FC, useEffect, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { Spinner } from 'components/loadings';
import Notification from 'components/notification';
import ActivitiesChartTabs from 'components/stats/activitiesChartTabs';
import CustomBarChart from 'components/stats/CustomBarChart';
import { getSeriesByTypeAction } from 'contexts/analytics/actions';
import { AggregationStatsData } from 'contexts/analytics/types';
import AnalyticsUserHeader from 'pages/Users/components/UserAnalytics/AnalyticsUserHeader';
import { barChartConfig } from 'pages/Users/components/UserAnalytics/configs';
import { TabsColumn, UserAnalyticsRow } from './styles';

interface UserAnalyticsProps {
  aggregationTabs: AggregationStatsData[];
  aggregationSeries: any[];
}

const UserRowAnalytics: FC<UserAnalyticsProps> = ({ aggregationTabs, aggregationSeries }) => {
  const [fetching, setFetching] = useState(false);
  const [aggregationSelected, setAggregationSelected] = useState(aggregationTabs[0]);
  const [graphData, setGraphData] = useState({
    name: null,
    items: [],
    labels: [],
  });
  const chartAreaRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    getSeriesData(aggregationSelected);
  }, []);

  // Keep the table from scrolling horizontally underneath the chart when the
  // pointer is over it. React's onWheel is passive, so attach manually with
  // passive: false to allow preventDefault. Vertical (page) scroll passes through.
  useEffect(() => {
    const el = chartAreaRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if (e.shiftKey || Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
        e.preventDefault();
      }
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  const getSeriesData = async (aggregation: AggregationStatsData) => {
    const aggregationSerie = aggregationSeries.find((as) => as.name === aggregation.name);
    setGraphData((prevState) => ({
      ...prevState,
      name: aggregationSerie.name,
      labels: aggregationSerie.series.map((s) => ({ key: s.key, label: s.label, color: s.color })),
    }));
    setFetching(true);
    try {
      await Promise.all(
        aggregationSerie.series.map(async (s) => {
          try {
            const response = await getSeriesByTypeAction(s.params);
            if (response instanceof Error) {
              Notification({ text: `Error retrieving ${s.label} stat`, type: 'error' });
            } else {
              setGraphData((prevState) => {
                if (prevState.items.length) {
                  return {
                    ...prevState,
                    items: prevState.items.map((it, index) => ({
                      ...it,
                      [s.key]: response?.results[index]?.count,
                    })),
                  };
                } else {
                  return {
                    ...prevState,
                    items: response?.results?.map((r, index) => ({
                      date: `Week ${index + 1}`,
                      [s.key]: r.count,
                    })),
                  };
                }
              });
            }
          } catch (error) {
            Notification({ text: `Error retrieving ${s.label} stat`, type: 'error' });
          }
        })
      );
    } finally {
      setFetching(false);
    }
  };

  const handleTabChanged = (name: string) => {
    setGraphData({
      name: null,
      items: [],
      labels: [],
    });
    const aggregation = aggregationTabs.find((ag) => ag.name === name);
    setAggregationSelected(aggregation);
    getSeriesData(aggregation);
  };

  return (
    <UserAnalyticsRow className="user-analytics-row" columns="25% 75%">
      <TabsColumn>
        <ActivitiesChartTabs
          vertical={true}
          title={'User Stats'}
          aggregationTabs={aggregationTabs}
          onTabChanged={handleTabChanged}
        />
      </TabsColumn>
      <div ref={chartAreaRef}>
        <Spinner
          active={fetching}
          top={isMobile ? '150px' : '250px'}
          left="50%"
          position="relative"
        />
        <AnalyticsUserHeader title={aggregationSelected.label} labels={graphData.labels} />
        <CustomBarChart chartConfig={barChartConfig} barData={graphData} />
      </div>
    </UserAnalyticsRow>
  );
};

export default UserRowAnalytics;
